import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAiJobs } from "@/context/AiJobsContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, Legend,
} from "recharts";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Loader2, FileText, Target, Map, Rocket, CheckCircle2, Gauge, Plus, Pencil, Trash2, Activity, Link2, Copy, ClipboardList, Building2, ListChecks, Wand2, AlertTriangle } from "lucide-react";

const scoreColor = (s) => (s >= 70 ? "#00A859" : s >= 40 ? "#F59E0B" : "#FF2A2A");
const impactBadge = {
  ALTO: "bg-destructive/10 text-destructive", MEDIO: "bg-[#F59E0B]/10 text-[#B45309]", BASSO: "bg-[#00A859]/10 text-[#00784A]",
};
const PHASES = [
  { key: "fase_0_3", label: "0–3 mesi", sub: "Fondamenta: dati/processi" },
  { key: "fase_3_6", label: "3–6 mesi", sub: "Primi strumenti AI/CRM" },
  { key: "fase_6_12", label: "6–12 mesi", sub: "Automazione e formazione" },
];

export default function ReadinessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit } = useAuth();
  const { trackJob } = useAiJobs();
  const [a, setA] = useState(null);
  const [offers, setOffers] = useState([]);
  const [benchmark, setBenchmark] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [qLink, setQLink] = useState(null);
  const [qDialog, setQDialog] = useState(false);
  const [qLoading, setQLoading] = useState(false);

  const load = async () => {
    const [res, fw, bm] = await Promise.all([
      api.get(`/readiness/${id}`),
      api.get("/readiness/framework"),
      api.get(`/readiness/${id}/benchmark`).catch(() => ({ data: null })),
    ]);
    setA(res.data); setOffers(fw.data.offer_tiers); setBenchmark(bm.data);
  };
  useEffect(() => { load(); }, [id]);

  const genQuestionnaire = async () => {
    setQLoading(true); setQDialog(true);
    try {
      const { data } = await api.post(`/readiness/${id}/questionnaire`);
      setQLink(`${window.location.origin}/q/${data.token}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setQDialog(false); }
    finally { setQLoading(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const data = await trackJob(api.post(`/readiness/${id}/generate`), { label: "Deliverable AI", link: `/readiness/${id}` });
      setA((prev) => ({ ...prev, ai_deliverable: data }));
      toast.success("Deliverable generato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setGenerating(false); }
  };

  if (!a) return <Layout><PageHeader title="Assessment" /><div className="flex justify-center py-24"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div></Layout>;

  const radarData = (a.breakdown || []).map((b) => {
    const bp = benchmark?.per_area?.find((p) => p.key === b.key);
    return { area: b.name, score: b.score, benchmark: bp?.benchmark ?? null, market: bp?.market ?? null };
  });
  const hasBench = benchmark && benchmark.benchmark_overall != null;
  const hasMarket = benchmark && benchmark.market_overall != null;
  const d = a.ai_deliverable;
  const tier = a.tier || {};

  return (
    <Layout>
      <PageHeader title={a.client_name} subtitle={a.sector || "AI & Sales Readiness"}>
        <Button variant="outline" className="rounded-none" onClick={() => navigate("/readiness")}><ArrowLeft className="w-4 h-4 mr-2" /> Indietro</Button>
        <Button variant="outline" className="rounded-none" data-testid="readiness-pdf-btn" onClick={() => window.open(`${API}/readiness/${id}/report.pdf`, "_blank")}><FileText className="w-4 h-4 mr-2" /> Report PDF</Button>
        <Button variant="outline" className="rounded-none" data-testid="onepager-btn" onClick={() => navigate(`/readiness/${id}/onepager`)}><FileText className="w-4 h-4 mr-2" /> One-page</Button>
        {canEdit && (
          <Button variant="outline" className="rounded-none" data-testid="questionnaire-link-btn" onClick={genQuestionnaire}><ClipboardList className="w-4 h-4 mr-2" /> Link questionario</Button>
        )}
        {canEdit && (
          <Button className="rounded-none" data-testid="generate-readiness-btn" onClick={generate} disabled={generating}>
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Genera deliverable
          </Button>
        )}
      </PageHeader>

      <div className="p-8 space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white border border-border p-6">
            <div className="data-label">Punteggio complessivo</div>
            <div className="font-mono font-medium text-6xl mt-2" style={{ color: scoreColor(a.overall_score) }} data-testid="readiness-overall">{a.overall_score}</div>
            <div className="text-xs text-muted-foreground">/ 100 (media pesata)</div>
            {tier.label && (
              <span className="inline-block mt-3 px-3 py-1 text-xs font-bold tracking-wide text-white" style={{ backgroundColor: tier.color }} data-testid="maturity-tier">
                {tier.label} · {tier.level}/5
              </span>
            )}
            {hasBench && (
              <div className="mt-3 text-xs text-muted-foreground" data-testid="benchmark-summary">
                Media {benchmark.basis === "settore" ? `settore ${benchmark.sector}` : "portfolio"} ({benchmark.peer_count}): <span className="font-mono font-semibold text-foreground">{benchmark.benchmark_overall}</span>
                <span className={a.overall_score >= benchmark.benchmark_overall ? "text-[#00A859] ml-1" : "text-destructive ml-1"}>
                  ({a.overall_score >= benchmark.benchmark_overall ? "+" : ""}{Math.round((a.overall_score - benchmark.benchmark_overall) * 10) / 10})
                </span>
              </div>
            )}
            {hasMarket && (
              <div className="mt-1 text-xs text-muted-foreground" data-testid="market-summary">
                Media mercato settore: <span className="font-mono font-semibold text-foreground">{benchmark.market_overall}</span>
                <span className={a.overall_score >= benchmark.market_overall ? "text-[#00A859] ml-1" : "text-destructive ml-1"}>
                  ({a.overall_score >= benchmark.market_overall ? "+" : ""}{Math.round((a.overall_score - benchmark.market_overall) * 10) / 10})
                </span>
              </div>
            )}
            {a.client_id && (
              <button onClick={() => navigate(`/clients/${a.client_id}`)} className="mt-3 text-sm text-primary hover:underline flex items-center gap-1" data-testid="client-link">
                <Building2 className="w-3.5 h-3.5" /> Vai al cliente
              </button>
            )}
            <div className="w-full h-2 bg-secondary mt-4"><div className="h-2" style={{ width: `${a.overall_score}%`, background: scoreColor(a.overall_score) }} /></div>
            <div className="mt-6 space-y-2 border-t border-border pt-4">
              {(a.breakdown || []).map((b) => (
                <div key={b.key} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{b.name} <span className="font-mono text-xs">({b.weight}%)</span></span>
                  <span className="font-mono font-semibold">{b.score}/5</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 bg-white border border-border p-6" data-testid="readiness-radar">
            <h3 className="font-heading font-bold text-lg tracking-tight mb-2">Profilo di maturità</h3>
            <div className="data-label mb-4">Score per area (1–5){hasBench ? " · cliente vs benchmark" : ""}{hasMarket ? " · vs mercato" : ""}</div>
            <ResponsiveContainer width="100%" height={340}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#E5E7EB" />
                <PolarAngleAxis dataKey="area" tick={{ fontSize: 12, fill: "#4B5563" }} />
                <PolarRadiusAxis domain={[0, 5]} tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#9CA3AF" />
                <Radar name="Cliente" dataKey="score" stroke="#002FA7" fill="#002FA7" fillOpacity={0.25} strokeWidth={2} />
                {hasBench && <Radar name={`Media ${benchmark.basis === "settore" ? benchmark.sector : "portfolio"}`} dataKey="benchmark" stroke="#00A859" fill="#00A859" fillOpacity={0.08} strokeWidth={2} strokeDasharray="4 3" />}
                {hasMarket && <Radar name="Mercato (settore)" dataKey="market" stroke="#9333EA" fill="#9333EA" fillOpacity={0.05} strokeWidth={2} strokeDasharray="2 2" />}
                {(hasBench || hasMarket) && <Legend />}
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-border p-6" data-testid="readiness-deliverable">
          <div className="flex items-center gap-2 mb-4"><Sparkles className="w-4 h-4 text-primary" /><h3 className="font-heading font-bold text-lg tracking-tight">Deliverable AI</h3></div>
          {!d ? (
            <p className="text-sm text-muted-foreground">Nessun deliverable generato. {canEdit ? 'Clicca "Genera deliverable" per ottenere sintesi, 3 priorità, roadmap a 3 fasi e raccomandazione dell\'offerta (Claude Sonnet 4.6).' : "Chiedi a un membro o admin di generarlo."}</p>
          ) : (
            <div className="space-y-8">
              <p className="text-sm">{d.summary}</p>

              {d.top_priorities?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 data-label mb-3"><Target className="w-3.5 h-3.5 text-primary" /> Le 3 priorità (quick win)</div>
                  <div className="grid md:grid-cols-3 gap-3">
                    {d.top_priorities.map((p, i) => (
                      <div key={i} className="border border-border p-4">
                        <div className="flex items-center justify-between">
                          <span className="data-label">{p.area}</span>
                          <span className="font-mono text-xs text-muted-foreground">#{i + 1}</span>
                        </div>
                        <div className="font-medium text-sm mt-1">{p.title}</div>
                        <p className="text-xs text-muted-foreground mt-1">{p.why}</p>
                        <div className="flex gap-2 mt-2">
                          <span className={`px-2 py-0.5 text-[10px] font-bold ${impactBadge[p.impact] || "bg-secondary"}`}>Impatto {p.impact}</span>
                          <span className={`px-2 py-0.5 text-[10px] font-bold ${impactBadge[p.effort] || "bg-secondary"}`}>Sforzo {p.effort}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {d.roadmap && (
                <div>
                  <div className="flex items-center gap-2 data-label mb-3"><Map className="w-3.5 h-3.5 text-primary" /> Roadmap</div>
                  <div className="grid md:grid-cols-3 gap-3">
                    {PHASES.map((ph) => (
                      <div key={ph.key} className="border border-border">
                        <div className="bg-primary text-primary-foreground px-3 py-2">
                          <div className="font-heading font-bold text-sm tracking-tight">{ph.label}</div>
                          <div className="text-[10px] opacity-80">{ph.sub}</div>
                        </div>
                        <ul className="p-3 space-y-2 text-sm list-disc pl-6">
                          {(d.roadmap[ph.key] || []).map((x, i) => <li key={i}>{x}</li>)}
                          {(!d.roadmap[ph.key] || d.roadmap[ph.key].length === 0) && <li className="list-none text-muted-foreground text-xs">—</li>}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {d.next_step_recommendation && (
                <div className="border-l-2 border-primary bg-secondary/50 p-4">
                  <div className="flex items-center gap-2 data-label mb-2"><Rocket className="w-3.5 h-3.5 text-primary" /> Raccomandazione — Next step</div>
                  <p className="text-sm">{d.next_step_recommendation}</p>
                  {d.recommended_offer && (
                    <div className="mt-3 flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-[#00A859]" />
                      <span><b>Offerta consigliata:</b> {d.recommended_offer.level} — <span className="text-muted-foreground">{d.recommended_offer.reason}</span></span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border border-border p-6" data-testid="offer-tiers">
          <div className="data-label mb-3">Offerta commerciale (modello a imbuto)</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left">
                <th className="data-label px-4 py-2">Livello</th>
                <th className="data-label px-4 py-2">Include</th>
                <th className="data-label px-4 py-2">Durata</th>
                <th className="data-label px-4 py-2 text-right">Prezzo</th>
              </tr></thead>
              <tbody>
                {offers.map((o, i) => {
                  const recommended = d?.recommended_offer?.level && o.level.startsWith(d.recommended_offer.level);
                  return (
                    <tr key={i} className={`border-b border-border ${recommended ? "bg-primary/5" : i % 2 ? "bg-[#F9FAFB]" : "bg-white"}`}>
                      <td className="px-4 py-2 font-medium">{o.level} {recommended && <span className="ml-1 text-[10px] font-bold bg-primary text-primary-foreground px-1.5 py-0.5">CONSIGLIATO</span>}</td>
                      <td className="px-4 py-2 text-muted-foreground">{o.includes}</td>
                      <td className="px-4 py-2 text-muted-foreground">{o.duration}</td>
                      <td className="px-4 py-2 text-right font-mono">{o.price}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="p-8 pt-0">
        <KpiTracker assessmentId={id} kpis={a.kpis || []} canEdit={canEdit} onChange={load} />
      </div>

      <div className="p-8 pt-0">
        <TaskTracker assessmentId={id} tasks={a.tasks || []} hasDeliverable={!!d} canEdit={canEdit} onChange={load} />
      </div>

      <Dialog open={qDialog} onOpenChange={setQDialog}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">Link questionario self-service</DialogTitle></DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-muted-foreground">Invia questo link al cliente: potrà compilare da solo il questionario delle 5 aree. I punteggi verranno registrati automaticamente in questa scheda.</p>
            {qLoading ? (
              <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
            ) : (
              <div className="flex items-center gap-2">
                <Input readOnly value={qLink || ""} className="rounded-none font-mono text-xs" data-testid="questionnaire-link-input" />
                <Button className="rounded-none" onClick={() => { navigator.clipboard.writeText(qLink); toast.success("Link copiato"); }} data-testid="copy-questionnaire-link"><Copy className="w-4 h-4" /></Button>
              </div>
            )}
            <p className="text-xs text-muted-foreground">Il link è monouso e scade dopo la compilazione.</p>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

const emptyKpi = { name: "", unit: "", baseline: 0, target: "", higher_is_better: true };

function KpiTracker({ assessmentId, kpis, canEdit, onChange }) {
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(emptyKpi);
  const [editId, setEditId] = useState(null);
  const [reading, setReading] = useState({ open: false, kpiId: null, value: "", note: "" });
  const [busy, setBusy] = useState(false);

  const seedDefaults = async () => {
    setBusy(true);
    try { await api.post(`/readiness/${assessmentId}/kpis/seed-defaults`); toast.success("KPI predefiniti aggiunti"); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openNew = () => { setForm(emptyKpi); setEditId(null); setDialog(true); };
  const openEdit = (k) => { setForm({ name: k.name, unit: k.unit || "", baseline: k.baseline ?? 0, target: k.target ?? "", higher_is_better: k.higher_is_better !== false }); setEditId(k.id); setDialog(true); };

  const saveKpi = async () => {
    if (!form.name.trim()) { toast.error("Nome KPI obbligatorio"); return; }
    const payload = { name: form.name, unit: form.unit, baseline: parseFloat(form.baseline) || 0, target: form.target === "" ? null : parseFloat(form.target), higher_is_better: form.higher_is_better };
    try {
      if (editId) await api.put(`/readiness/${assessmentId}/kpis/${editId}`, payload);
      else await api.post(`/readiness/${assessmentId}/kpis`, payload);
      toast.success("KPI salvato"); setDialog(false); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeKpi = async (kid) => {
    if (!window.confirm("Eliminare questo KPI?")) return;
    await api.delete(`/readiness/${assessmentId}/kpis/${kid}`); toast.success("KPI eliminato"); onChange();
  };

  const addReading = async () => {
    const v = parseFloat(reading.value);
    if (Number.isNaN(v)) { toast.error("Inserisci un valore numerico"); return; }
    try {
      await api.post(`/readiness/${assessmentId}/kpis/${reading.kpiId}/readings`, { value: v, note: reading.note });
      toast.success("Rilevazione aggiunta"); setReading({ open: false, kpiId: null, value: "", note: "" }); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-white border border-border p-6" data-testid="kpi-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 data-label"><Gauge className="w-3.5 h-3.5 text-primary" /> KPI & Risultati (casi studio)</div>
        {canEdit && (
          <div className="flex gap-2">
            {kpis.length === 0 && <Button size="sm" variant="outline" className="rounded-none" onClick={seedDefaults} disabled={busy} data-testid="seed-kpis-btn">{busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}KPI predefiniti</Button>}
            <Button size="sm" className="rounded-none" onClick={openNew} data-testid="add-kpi-btn"><Plus className="w-4 h-4 mr-1" /> Nuovo KPI</Button>
          </div>
        )}
      </div>

      {kpis.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nessun KPI. Aggiungi i KPI predefiniti (ore risparmiate, conversion, ricavi) o creane di custom per documentare i risultati ottenuti.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {kpis.map((k) => {
            const pos = (k.improvement ?? 0) >= 0;
            return (
              <div key={k.id} className="border border-border p-4" data-testid={`kpi-card-${k.name}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium text-sm">{k.name}</div>
                    <div className="data-label">{k.unit}</div>
                  </div>
                  {canEdit && (
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(k)} className="text-muted-foreground hover:text-primary"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => removeKpi(k.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
                <div className="flex items-end gap-3 mt-3">
                  <div>
                    <div className="data-label">Baseline</div>
                    <div className="font-mono text-lg">{k.baseline ?? 0}</div>
                  </div>
                  <div className="text-muted-foreground pb-1">→</div>
                  <div>
                    <div className="data-label">Attuale</div>
                    <div className="font-mono text-lg font-semibold">{k.current ?? 0}</div>
                  </div>
                  <div className="ml-auto text-right">
                    <div className={`font-mono font-bold ${pos ? "text-[#00A859]" : "text-destructive"}`}>{pos ? "+" : ""}{k.improvement ?? 0}</div>
                    {k.improvement_pct != null && <div className={`text-xs ${pos ? "text-[#00A859]" : "text-destructive"}`}>{pos ? "+" : ""}{k.improvement_pct}%</div>}
                  </div>
                </div>
                {k.target != null && <div className="text-xs text-muted-foreground mt-1">Target: <span className="font-mono">{k.target} {k.unit}</span></div>}

                {(k.readings || []).length >= 2 && (
                  <ResponsiveContainer width="100%" height={90}>
                    <LineChart data={k.readings.map((r) => ({ d: new Date(r.date).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }), v: r.value }))} margin={{ top: 8, right: 4, left: -20, bottom: 0 }}>
                      <XAxis dataKey="d" tick={{ fontSize: 9 }} stroke="#9CA3AF" />
                      <YAxis tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} stroke="#9CA3AF" width={30} />
                      <RTooltip contentStyle={{ borderRadius: 0, border: "1px solid #E5E7EB", fontSize: 11 }} />
                      <Line type="monotone" dataKey="v" stroke="#002FA7" strokeWidth={2} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}

                {canEdit && (
                  <Button size="sm" variant="outline" className="rounded-none w-full mt-3" onClick={() => setReading({ open: true, kpiId: k.id, value: "", note: "" })} data-testid={`add-reading-${k.name}`}>
                    <Activity className="w-3.5 h-3.5 mr-1" /> Aggiungi rilevazione
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">{editId ? "Modifica KPI" : "Nuovo KPI"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label className="data-label">Nome *</Label><Input data-testid="kpi-name-input" className="mt-1 rounded-none" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="es. Ore risparmiate / mese" /></div>
              <div><Label className="data-label">Unità</Label><Input className="mt-1 rounded-none" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="h/mese, %, €" /></div>
              <div><Label className="data-label">Baseline</Label><Input type="number" className="mt-1 rounded-none font-mono" value={form.baseline} onChange={(e) => setForm({ ...form, baseline: e.target.value })} /></div>
              <div><Label className="data-label">Target</Label><Input type="number" className="mt-1 rounded-none font-mono" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setDialog(false)}>Annulla</Button>
            <Button className="rounded-none" onClick={saveKpi} data-testid="save-kpi-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reading.open} onOpenChange={(o) => setReading((r) => ({ ...r, open: o }))}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">Nuova rilevazione</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="data-label">Valore *</Label><Input type="number" data-testid="reading-value-input" className="mt-1 rounded-none font-mono" value={reading.value} onChange={(e) => setReading({ ...reading, value: e.target.value })} /></div>
            <div><Label className="data-label">Nota</Label><Input className="mt-1 rounded-none" value={reading.note} onChange={(e) => setReading({ ...reading, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setReading({ open: false, kpiId: null, value: "", note: "" })}>Annulla</Button>
            <Button className="rounded-none" onClick={addReading} data-testid="save-reading-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


const PHASE_LABELS = { priorita: "Priorità", fase_0_3: "0–3 mesi", fase_3_6: "3–6 mesi", fase_6_12: "6–12 mesi", manuale: "Manuale" };
const PHASE_ORDER = ["priorita", "fase_0_3", "fase_3_6", "fase_6_12", "manuale"];
const STATUS_META = {
  todo: { label: "Da fare", cls: "bg-secondary text-secondary-foreground" },
  doing: { label: "In corso", cls: "bg-[#F59E0B] text-white" },
  done: { label: "Completato", cls: "bg-[#00A859] text-white" },
};
const emptyTask = { title: "", assignee: "", due_date: "", phase: "manuale", status: "todo" };
const todayISO = () => new Date().toISOString().slice(0, 10);
const isOverdue = (t) => t.status !== "done" && t.due_date && t.due_date < todayISO();

function TaskTracker({ assessmentId, tasks, hasDeliverable, canEdit, onChange }) {
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(emptyTask);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const openNew = () => { setForm(emptyTask); setEditId(null); setDialog(true); };
  const openEdit = (t) => { setForm({ title: t.title, assignee: t.assignee || "", due_date: t.due_date || "", phase: t.phase || "manuale", status: t.status || "todo" }); setEditId(t.id); setDialog(true); };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/readiness/${assessmentId}/tasks/generate`);
      toast.success(data.created > 0 ? `${data.created} attività create dalla roadmap` : "Nessuna nuova attività (già presenti)");
      onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titolo attività obbligatorio"); return; }
    try {
      if (editId) await api.put(`/readiness/${assessmentId}/tasks/${editId}`, form);
      else await api.post(`/readiness/${assessmentId}/tasks`, form);
      toast.success("Attività salvata"); setDialog(false); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeStatus = async (t, status) => {
    try { await api.put(`/readiness/${assessmentId}/tasks/${t.id}`, { ...t, status }); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (tid) => {
    if (!window.confirm("Eliminare questa attività?")) return;
    await api.delete(`/readiness/${assessmentId}/tasks/${tid}`); toast.success("Attività eliminata"); onChange();
  };

  const sorted = [...tasks].sort((a, b) => PHASE_ORDER.indexOf(a.phase) - PHASE_ORDER.indexOf(b.phase) || (a.due_date || "").localeCompare(b.due_date || ""));
  const doneCount = tasks.filter((t) => t.status === "done").length;
  const overdueCount = tasks.filter(isOverdue).length;
  const pct = tasks.length ? Math.round((doneCount / tasks.length) * 100) : 0;

  return (
    <div className="bg-white border border-border p-6" data-testid="task-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2 data-label"><ListChecks className="w-3.5 h-3.5 text-primary" /> Piano operativo &amp; follow-up</div>
        {canEdit && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-none" onClick={generate} disabled={busy || !hasDeliverable} title={hasDeliverable ? "Crea attività dalla roadmap AI" : "Genera prima il deliverable AI"} data-testid="generate-tasks-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />} Genera dalla roadmap
            </Button>
            <Button size="sm" className="rounded-none" onClick={openNew} data-testid="add-task-btn"><Plus className="w-4 h-4 mr-1" /> Nuova attività</Button>
          </div>
        )}
      </div>

      {tasks.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nessuna attività. {canEdit ? 'Genera automaticamente le attività dalla roadmap AI oppure aggiungile manualmente, con assegnatario e scadenza.' : "Nessun task assegnato."}</p>
      ) : (
        <>
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>Avanzamento</span>
                <span className="font-mono" data-testid="task-progress">{doneCount}/{tasks.length} · {pct}%</span>
              </div>
              <div className="w-full h-2 bg-secondary"><div className="h-2 bg-[#00A859]" style={{ width: `${pct}%` }} /></div>
            </div>
            {overdueCount > 0 && (
              <span className="flex items-center gap-1 text-xs font-bold text-destructive" data-testid="task-overdue-count">
                <AlertTriangle className="w-3.5 h-3.5" /> {overdueCount} in ritardo
              </span>
            )}
          </div>

          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left">
                <th className="data-label px-4 py-2">Attività</th>
                <th className="data-label px-4 py-2">Fase</th>
                <th className="data-label px-4 py-2">Assegnatario</th>
                <th className="data-label px-4 py-2">Scadenza</th>
                <th className="data-label px-4 py-2">Stato</th>
                {canEdit && <th className="data-label px-4 py-2 text-right"></th>}
              </tr></thead>
              <tbody>
                {sorted.map((t, i) => {
                  const od = isOverdue(t);
                  const sm = STATUS_META[t.status] || STATUS_META.todo;
                  return (
                    <tr key={t.id} data-testid={`task-row-${i}`} className={`border-b border-border ${i % 2 ? "bg-[#F9FAFB]" : "bg-white"} ${t.status === "done" ? "opacity-60" : ""}`}>
                      <td className={`px-4 py-2 ${t.status === "done" ? "line-through" : ""}`}>{t.title}</td>
                      <td className="px-4 py-2"><span className="text-[10px] font-bold bg-secondary px-2 py-0.5">{PHASE_LABELS[t.phase] || t.phase}</span></td>
                      <td className="px-4 py-2 text-muted-foreground">{t.assignee || "—"}</td>
                      <td className={`px-4 py-2 font-mono text-xs ${od ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
                        {t.due_date ? new Date(t.due_date).toLocaleDateString("it-IT") : "—"}{od && " ⚠"}
                      </td>
                      <td className="px-4 py-2">
                        {canEdit ? (
                          <Select value={t.status} onValueChange={(v) => changeStatus(t, v)}>
                            <SelectTrigger data-testid={`task-status-${i}`} className="w-[130px] h-7 rounded-none text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent className="rounded-none">
                              {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`text-[10px] font-bold px-2 py-0.5 ${sm.cls}`}>{sm.label}</span>
                        )}
                      </td>
                      {canEdit && (
                        <td className="px-4 py-2">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => openEdit(t)} className="text-muted-foreground hover:text-primary"><Pencil className="w-3.5 h-3.5" /></button>
                            <button onClick={() => remove(t.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">{editId ? "Modifica attività" : "Nuova attività"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="data-label">Titolo *</Label><Input data-testid="task-title-input" className="mt-1 rounded-none" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="es. Centralizzare l'anagrafica clienti nel CRM" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="data-label">Assegnatario</Label><Input className="mt-1 rounded-none" value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} placeholder="Nome / ruolo" /></div>
              <div><Label className="data-label">Scadenza</Label><Input type="date" data-testid="task-due-input" className="mt-1 rounded-none font-mono" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              <div>
                <Label className="data-label">Fase</Label>
                <Select value={form.phase} onValueChange={(v) => setForm({ ...form, phase: v })}>
                  <SelectTrigger className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {PHASE_ORDER.map((k) => <SelectItem key={k} value={k}>{PHASE_LABELS[k]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="data-label">Stato</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setDialog(false)}>Annulla</Button>
            <Button className="rounded-none" onClick={save} data-testid="save-task-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
